import { CanActivateFn, Router } from '@angular/router';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UserStateService } from 'riterz-core';
import { filter, map, take } from 'rxjs';

/**
 * Requires the authenticated user to have the `super-admin` role.
 *
 * **Layer 3** — always placed after `loggedInGuard` and `approvedGuard`.
 * Waits for `currentUser` to emit, checks `user.roles` for `super-admin`,
 * and redirects to `/` if the role is missing.
 *
 * SSR: returns `true` (user data unavailable server-side).
 *
 * Currently applied only to the `/admin` parent route — all child routes
 * inherit the guard chain `[ loggedInGuard, approvedGuard, superAdminGuard ]`.
 */
export const superAdminGuard: CanActivateFn = (
  route,
  state,
  platformId = inject(PLATFORM_ID),
  router: Router = inject(Router),
  userStateService: UserStateService = inject(UserStateService),
) => {
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  return userStateService.currentUser.pipe(
    filter(user => user !== null),
    take(1),
    map(user => {
      const isSuperAdmin = !!user.roles?.find(r => r.name === 'super-admin');
      if (!isSuperAdmin) {
        router.navigate(['/']);
        return false;
      }
      return true;
    })
  );
};
